// KidProfilesPanel — Jul 2026 (Randy). Parent-side kid profile management,
// rendered inside the calendar Household panel. Kids need no email: access is
// via the family picker (tile + PIN) or a magic link / QR code.
//
// Feb 2026 (Randy) — kids can now be dialed between the same 3 add-modes as
// email invitees (Adds directly / Needs approval / Can't add). Approval-
// always stays the default. Rows also gain Chores + Schedule pill buttons
// (handled by parent HouseholdPanel via onChoresClick / onScheduleClick
// callbacks so both modals live in one place).
import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Baby, Plus, QrCode, Trash, X, ArrowsClockwise, Copy, PencilSimple, Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import MasterEmojiGeneratorModal from "@/components/MasterEmojiGeneratorModal";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

// Feb 2026 — the emoji list now spans childish → teen so older kids don't
// feel stuck with cartoony icons. First row = playful/childish (kept intact
// for younger kids); second row = teen-leaning options.
const EMOJIS = [
  // childish (kept)
  "🙂", "🦖", "🦄", "🐯", "🐸", "🚀", "🌈", "⚽", "🎨", "🐱",
  // teen-leaning
  "🎧", "🎮", "🛹", "🏀", "🎸", "🎤", "📱", "🕶️", "🧢", "👟",
  "🎬", "🎯", "⚡", "🔥", "💯", "🏆", "🌟", "✨", "💻", "🎹",
];

// Feb 2026 (Randy) — mirrored from CalendarSection's COLLAB_MODE_OPTS but
// kept local so KidProfilesPanel isn't coupled to its parent's exports.
// pt89b: default kid pill "Approval always" → "Needs approval" AND its
// visual style matches the "Adds directly" pill (green text / white
// outline) so kid rows stop feeling different from email-invitee rows.
const KID_MODE_OPTS = [
  { v: "direct",   label: "Adds directly",  short: "Adds directly",   hint: "trusted kid — items land straight on the calendar",
    pillCls: "bg-[#EAF3EC] text-[#2F6B3A] border border-[#B9D4BE]" },
  { v: "approval", label: "Needs approval", short: "Needs approval",  hint: "the safe default — items wait in your inbox",
    pillCls: "bg-white text-[#2F6B3A] border border-[#4A5D4E]" },
  { v: "off",      label: "Can't add",      short: "Can't add",       hint: "view only — no proposals allowed",
    pillCls: "bg-[#FBEBE4] text-[#A55A48] border border-[#EACAB8]" },
];

export default function KidProfilesPanel({ siloId, kids: kidsFromParent, onChoresClick, onScheduleClick, onReload }) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep. All visible family/kid
  // vocabulary resolves through the theme map (tooltips stay theme-agnostic).
  const tKidProfiles = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_PROFILES);
  const tChoresBtn   = useThemeLabel(T.THEME_KEY_HOUSEHOLD_CHORES_BUTTON);
  const tScheduleBtn = useThemeLabel(T.THEME_KEY_HOUSEHOLD_SCHEDULE_BUTTON);
  const tAddKid      = useThemeLabel(T.THEME_KEY_HOUSEHOLD_ADD_KID);
  const tKidNamePh   = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_NAME_PLACEHOLDER);
  const tHelperIntro = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_HELPER_INTRO);
  const tPickerLink  = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_PICKER_LINK);
  const tHelperRest  = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_HELPER_REST);
  const [kids, setKids] = useState([]);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [emoji, setEmoji] = useState("🙂");
  // pt89T (Feb 20, 2026, Randy) — Master Emoji Generator hook for the
  // kid-add row. Tapping the emoji square opens MEG (nudge + AI batch +
  // silo library). preserveParent=true so a pick keeps the add row's
  // context (name / PIN still filled in). The old hardcoded emoji
  // dropdown stays as a fallback picker (see below).
  const [megOpen, setMegOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkFor, setLinkFor] = useState(null); // {kid, url}
  const [editKid, setEditKid] = useState(null); // {id, name, emoji, pin, clearPin}
  const [mergeState, setMergeState] = useState(null); // {kid, info, targetId, busy}
  // Feb 2026 (Randy) — inline mode picker (mirrors family invitees).
  const [editingModeId, setEditingModeId] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);

  const load = () => api.get(`/family/${siloId}/kids`).then((r) => setKids(r.data.kids || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siloId]);
  const reloadAll = () => { load(); onReload && onReload(); };

  // Merge the parent-provided household member rows (which carry per-kid
  // add mode from /calendar/{silo}/settings) with the local kid list.
  const modeByKidId = {};
  (kidsFromParent || []).forEach((m) => {
    const rawId = (m.user_id || "").startsWith("kid:") ? m.user_id.slice(4) : null;
    if (rawId) modeByKidId[rawId] = m.mode || "approval";
  });

  const saveEdit = async () => {
    if (!editKid.name.trim()) { toast.error("Name can't be empty."); return; }
    if (editKid.pin && !/^\d{4,6}$/.test(editKid.pin)) { toast.error("PIN must be 4-6 digits."); return; }
    const body = { name: editKid.name.trim(), emoji: editKid.emoji };
    if (editKid.clearPin) body.pin = "";
    else if (editKid.pin) body.pin = editKid.pin;
    try {
      await api.patch(`/family/${siloId}/kids/${editKid.id}`, body);
      toast.success("Saved.");
      setEditKid(null);
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  const add = async () => {
    if (!name.trim()) return;
    if (pin && !/^\d{4,6}$/.test(pin)) { toast.error("PIN must be 4-6 digits."); return; }
    setBusy(true);
    try {
      await api.post(`/family/${siloId}/kids`, { name: name.trim(), pin: pin || null, emoji });
      setName(""); setPin(""); setEmoji("🙂");
      toast.success("Kid profile added — grab their QR code to set up their device.");
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); }
    finally { setBusy(false); }
  };
  const del = async (kid) => {
    // Kid Merge (Randy) — before losing a family-picker kid's points/chores/
    // badges, check if there's a family member to move them onto.
    let info = null;
    try { info = (await api.get(`/family/${siloId}/kids/${kid.id}/merge-info`)).data; }
    catch { /* fall back to a plain delete */ }
    const worthMoving = info && (info.summary.points > 0 || info.summary.chore_count > 0);
    if (info && worthMoving && (info.candidates || []).length > 0) {
      setMergeState({ kid, info, targetId: info.candidates[0].user_id, busy: false });
      return;
    }
    if (!window.confirm(`Remove ${kid.name}'s profile? Their device access stops immediately.`)) return;
    try { await api.delete(`/family/${siloId}/kids/${kid.id}`); toast.success("Removed."); reloadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
  };
  const doPlainDelete = async () => {
    const kid = mergeState.kid;
    setMergeState({ ...mergeState, busy: true });
    try { await api.delete(`/family/${siloId}/kids/${kid.id}`); toast.success("Removed."); setMergeState(null); reloadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); setMergeState({ ...mergeState, busy: false }); }
  };
  const doMigrate = async () => {
    const { kid, targetId } = mergeState;
    setMergeState({ ...mergeState, busy: true });
    try {
      const r = await api.post(`/family/${siloId}/kids/${kid.id}/migrate`, { target_user_id: targetId });
      toast.success(`Moved to ${r.data.target_name} — ${r.data.chores_moved} chore${r.data.chores_moved === 1 ? "" : "s"} carried over.`);
      setMergeState(null); reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't move."); setMergeState({ ...mergeState, busy: false }); }
  };
  const setLimit = async (kid, v) => {
    try { await api.patch(`/family/${siloId}/kids/${kid.id}`, { daily_ask_limit: Number(v) }); load(); }
    catch { toast.error("Couldn't save."); }
  };
  const openLink = async (kid) => {
    try {
      const r = await api.post(`/family/${siloId}/kids/${kid.id}/link`);
      setLinkFor({ kid, url: `${window.location.origin}${r.data.url}` });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't make a link."); }
  };
  const resetLinks = async (kid) => {
    if (!window.confirm("Reset links? Every QR code and signed-in device for this kid stops working.")) return;
    try {
      await api.post(`/family/${siloId}/kids/${kid.id}/reset-links`);
      toast.success("Old links are dead — make a fresh QR when ready.");
      setLinkFor(null);
    } catch { toast.error("Couldn't reset."); }
  };
  const startEditMode = (k) => {
    setEditingModeId(k.id);
    setPendingMode(modeByKidId[k.id] || "approval");
  };
  const cancelEditMode = () => { setEditingModeId(null); setPendingMode(null); };
  const savePendingMode = async (k) => {
    if (!pendingMode) { cancelEditMode(); return; }
    try {
      await api.post(`/calendar/${siloId}/collab-mode`, { user_id: `kid:${k.id}`, mode: pendingMode });
      toast.success(`${k.name}: ${KID_MODE_OPTS.find((o) => o.v === pendingMode)?.label || pendingMode}.`);
      cancelEditMode();
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  // Row helper: find the household-member row for a kid so we can pass its
  // excluded_chore_ids + availability into the modals HouseholdPanel opens.
  const memberRowFor = (k) => {
    // Prefer the fresh /kids row (has availability_notes) but fall back to
    // whatever the parent passed.
    const parentRow = (kidsFromParent || []).find((m) => (m.user_id || "").endsWith(`:${k.id}`));
    return {
      user_id: `kid:${k.id}`,
      name: k.name,
      emoji: k.emoji,
      is_kid: true,
      excluded_chore_ids: k.excluded_chore_ids || parentRow?.excluded_chore_ids || [],
      availability: k.availability || parentRow?.availability || null,
      availability_notes: k.availability_notes || parentRow?.availability_notes || null,
    };
  };

  return (
    <div data-testid="kid-profiles-panel">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1.5">
        <Baby size={12} weight="duotone" /> {tKidProfiles} (no login — PIN/QR only)
        <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={340} mobileCenter
          testid="kid-profiles-eye" label="How kid profiles work + the three add-modes">
          <strong>These are family-picker kid profiles.</strong> They sign in on a shared device with a picker tile + optional PIN, or by scanning their personal QR code — no email, no password of their own.{"\n\n"}
          Each kid has one of three add-modes — tap the pencil to change:{"\n\n"}
          <strong>Adds directly</strong> — trusted kids can add straight to the calendar without a gate.{"\n\n"}
          <strong>Approval always</strong> — the safe default. Anything they add waits in your <em>approval inbox</em> first.{"\n\n"}
          <strong>Can't add</strong> — view only. Perfect for the youngest kids or shared devices you don't want anyone adding on.
        </InfoTip>
      </div>
      {kids.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {kids.map((k) => {
            const kMode = modeByKidId[k.id] || "approval";
            const currentOpt = KID_MODE_OPTS.find((o) => o.v === kMode) || KID_MODE_OPTS[1];
            const isEditingMode = editingModeId === k.id;
            if (editKid?.id === k.id) {
              return (
              <div key={k.id} className="flex items-center gap-2 flex-wrap bg-[#F4F8F5] border border-[#B9D4BE] rounded px-2.5 py-2" data-testid={`kid-edit-row-${k.id}`}>
                <button type="button" onClick={() => setMegOpen(true)} data-testid="kid-edit-emoji"
                  title="Tap to open the Master Emoji Generator (AI batch + silo library + nudge)"
                  className="w-9 h-9 rounded-lg bg-white border-2 border-dashed border-[#C19A4A]/60 hover:border-[#C19A4A] hover:bg-[#F8F1E2] flex items-center justify-center text-xl leading-none transition-colors">
                  {editKid.emoji || "🙂"}
                </button>
                <input value={editKid.name} onChange={(e) => setEditKid({ ...editKid, name: e.target.value })} data-testid="kid-edit-name"
                  className="flex-1 min-w-[90px] px-2 py-1 border hairline rounded text-xs bg-white" />
                <input value={editKid.pin} onChange={(e) => setEditKid({ ...editKid, pin: e.target.value.replace(/\D/g, "").slice(0, 6), clearPin: false })}
                  placeholder={k.has_pin ? "new PIN" : "set PIN"} inputMode="numeric" data-testid="kid-edit-pin"
                  className="w-20 px-2 py-1 border hairline rounded text-xs bg-white" />
                {k.has_pin && (
                  <label className="text-[9px] text-[#6B6A68] flex items-center gap-1">
                    <input type="checkbox" checked={editKid.clearPin} onChange={(e) => setEditKid({ ...editKid, clearPin: e.target.checked, pin: "" })} data-testid="kid-edit-clearpin" />
                    no PIN
                  </label>
                )}
                <button onClick={saveEdit} data-testid="kid-edit-save"
                  className="text-[10px] px-2.5 py-1 rounded bg-[#4A5D4E] text-white">Save</button>
                <button onClick={() => setEditKid(null)} className="text-[10px] px-2 py-1 text-[#6B6A68]">Cancel</button>
              </div>
              );
            }
            if (isEditingMode) {
              return (
                <div key={k.id} className="flex items-center gap-2 flex-wrap bg-[#F4F8F5] border border-[#B9D4BE] rounded px-2.5 py-2" data-testid={`kid-mode-edit-${k.id}`}>
                  <span className="text-lg">{k.emoji}</span>
                  <span className="text-sm text-[#1C1B1A] flex-1 min-w-[80px] truncate">{k.name}</span>
                  <select value={pendingMode} onChange={(e) => setPendingMode(e.target.value)}
                    data-testid={`kid-mode-select-${k.id}`}
                    className="text-xs px-2 py-1.5 border hairline rounded bg-white focus:outline-none focus:border-[#4A5D4E]">
                    {KID_MODE_OPTS.map((o) => (
                      <option key={o.v} value={o.v}>{o.label} — {o.hint}</option>
                    ))}
                  </select>
                  <button onClick={() => savePendingMode(k)} data-testid={`kid-mode-save-${k.id}`}
                    className="text-[10px] px-2.5 py-1 rounded bg-[#4A5D4E] text-white">Save</button>
                  <button onClick={cancelEditMode} className="text-[10px] px-2 py-1 text-[#6B6A68]">Cancel</button>
                </div>
              );
            }
            return (
            <div key={k.id} className={`flex items-center gap-2 flex-wrap bg-white border hairline rounded px-2.5 py-2 ${k.exempt ? "opacity-60" : ""}`} data-testid={`kid-row-${k.id}`}>
              <span className={`text-lg ${k.exempt ? "grayscale" : ""}`}>{k.emoji}</span>
              <span className="text-sm text-[#1C1B1A] truncate min-w-[70px] shrink-0">{k.name}</span>
              {(onChoresClick || onScheduleClick) && (
                <div className="flex-1 flex items-center justify-center gap-1.5">
                  {onChoresClick && (
                    <button onClick={() => onChoresClick(memberRowFor(k))} data-testid={`kid-chores-btn-${k.id}`}
                      title="Which chores can this kid do?"
                      className="text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border border-[#B5B3AE] bg-gradient-to-b from-[#F5F4F1] to-[#E4E1DA] text-[#3A3A38] hover:from-[#EBEAE6] hover:to-[#D7D4CC] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(0,0,0,0.08)] font-medium">
                      {tChoresBtn}
                    </button>
                  )}
                  {/* Feb 26, 2026 (Randy pt-member-exempt) — hide/show
                      toggle. Owner-only (KidProfilesPanel is only visible
                      to parent-role, but the endpoint enforces
                      owner-only). Exempt kids stay in this panel with a
                      dimmed row so you can flip them back. */}
                  <button onClick={async () => {
                    const nextExempt = !k.exempt;
                    try {
                      await api.post(`/calendar/${siloId}/members/${encodeURIComponent("kid:" + k.id)}/exempt`, { exempt: nextExempt });
                      toast.success(nextExempt
                        ? `${k.name} is now hidden from scheduling.`
                        : `${k.name} is back in the mix.`);
                      reloadAll();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Couldn't update.");
                    }
                  }}
                    data-testid={`member-exempt-toggle-kid:${k.id}`}
                    title={k.exempt
                      ? "Hidden — click to put this kid back in the scheduling mix"
                      : "Active — click to hide from scheduling, leaderboard, and Smart Create (settings preserved)"}
                    className={`p-1.5 rounded-md border transition-colors ${
                      k.exempt
                        ? "bg-[#F4E4E4] border-[#D8B4B4] text-[#8A4A4A] hover:bg-[#EBD4D4]"
                        : "bg-white border-[#B5B3AE] text-[#3A3A38] hover:bg-[#F5F4F1]"
                    }`}>
                    {k.exempt ? <EyeSlash size={12} weight="duotone" /> : <Eye size={12} weight="duotone" />}
                  </button>
                  {onScheduleClick && (
                    <button onClick={() => onScheduleClick(memberRowFor(k))} data-testid={`kid-schedule-btn-${k.id}`}
                      title="When is this kid available?"
                      className="text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border border-[#B5B3AE] bg-gradient-to-b from-[#F5F4F1] to-[#E4E1DA] text-[#3A3A38] hover:from-[#EBEAE6] hover:to-[#D7D4CC] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(0,0,0,0.08)] font-medium">
                      {tScheduleBtn}
                    </button>
                  )}
                </div>
              )}
              <span className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 ${currentOpt.pillCls}`}
                title={currentOpt.hint} data-testid={`kid-mode-pill-${k.id}`}>
                {currentOpt.short}
              </span>
              <button onClick={() => startEditMode(k)} title="Change how their adds land"
                data-testid={`kid-mode-edit-${k.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                <PencilSimple size={13} />
              </button>
              <label className="text-[10px] text-[#6B6A68] flex items-center gap-1 shrink-0">
                <select value={k.daily_ask_limit} onChange={(e) => setLimit(k, e.target.value)} data-testid={`kid-limit-${k.id}`}
                  className="px-1 py-0.5 border hairline rounded bg-white text-[10px]">
                  {[0, 3, 5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                asks/day
              </label>
              <button onClick={() => setEditKid({ id: k.id, name: k.name, emoji: k.emoji, pin: "", clearPin: false })} title="Edit name, emoji or PIN" data-testid={`kid-edit-${k.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><PencilSimple size={14} /></button>
              <button onClick={() => openLink(k)} title="QR code & magic link" data-testid={`kid-qr-${k.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><QrCode size={15} /></button>
              {/* Feb 2026 (Randy) — Impersonation eye. Kids don't have a
                  standard JWT; the owner "steps in" via the family picker. */}
              <button onClick={() => { window.location.href = `/family/${siloId}`; }}
                title="Step into this kid's view (opens the family picker)"
                data-testid={`kid-impersonate-${k.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#2C6E92] hover:bg-[#E3EFF7]">
                <Eye size={14} weight="duotone" />
              </button>
              <button onClick={() => del(k)} title="Remove profile" data-testid={`kid-delete-${k.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]"><Trash size={14} /></button>
            </div>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {/* pt89T (Feb 20, 2026, Randy) — clickable emoji SQUARE opens the
            Master Emoji Generator. Falls back to the hardcoded picker
            dropdown next to it if the user wants a fast/silent choice. */}
        <button type="button" onClick={() => setMegOpen(true)}
          data-testid="kid-add-emoji-square"
          title="Tap to open the Master Emoji Generator (AI batch + silo library + nudge)"
          className="w-10 h-10 rounded-md border-2 border-dashed border-[#C19A4A]/50 hover:border-[#C19A4A] hover:bg-[#F8F1E2] bg-white flex items-center justify-center text-2xl leading-none transition-colors">
          {emoji || "🙂"}
        </button>
        <select value={emoji} onChange={(e) => setEmoji(e.target.value)} data-testid="kid-add-emoji"
          title="Quick picker (the AI generator lives on the emoji square to the left)"
          className="px-1.5 py-1.5 border hairline rounded bg-white text-xs">
          {EMOJIS.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={tKidNamePh}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid="kid-add-name"
          className="flex-1 min-w-[110px] px-2.5 py-1.5 border hairline rounded text-xs bg-white" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="PIN (optional)" inputMode="numeric" data-testid="kid-add-pin"
          className="w-24 px-2.5 py-1.5 border hairline rounded text-xs bg-white" />
        <button onClick={add} disabled={busy || !name.trim()} data-testid="kid-add-btn"
          className="text-[10px] px-2.5 py-1.5 rounded bg-[#1C1B1A] text-white disabled:opacity-50 flex items-center gap-1">
          <Plus size={11} weight="bold" /> {tAddKid}
        </button>
      </div>
      <div className="text-[10px] text-[#6B6A68] mt-1.5 leading-relaxed">
        {tHelperIntro} <a href={`/family/${siloId}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#1C1B1A]" data-testid="kid-open-picker">{tPickerLink}</a> {tHelperRest}
      </div>

      {linkFor && (
        <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLinkFor(null)}>
          <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-sm shadow-2xl max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="kid-link-modal">
            <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
              <h2 className="font-serif-display text-lg">{linkFor.kid.emoji} {linkFor.kid.name}'s device key</h2>
              <button onClick={() => setLinkFor(null)} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="kid-link-close"><X size={18} /></button>
            </div>
            <div className="p-5 flex-1 overflow-y-auto text-center">
              <div className="inline-block bg-white p-3 rounded-xl border hairline">
                <QRCodeCanvas value={linkFor.url} size={190} data-testid="kid-qr-canvas" />
              </div>
              <p className="text-xs text-[#6B6A68] mt-3 leading-relaxed">
                Scan this with the kid's device camera — it opens their kid page, no password ever.
                Works for {180} days; reset any time.
              </p>
              <button
                onClick={() => { navigator.clipboard?.writeText(linkFor.url); toast.success("Link copied."); }}
                data-testid="kid-link-copy"
                className="mt-3 text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5 mx-auto">
                <Copy size={12} /> Copy magic link instead
              </button>
            </div>
            <div className="px-5 py-3 border-t hairline shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
              <button onClick={() => resetLinks(linkFor.kid)} data-testid="kid-link-reset"
                className="text-[10px] text-[#C16E5A] underline flex items-center gap-1">
                <ArrowsClockwise size={11} /> Lost the device? Reset all links &amp; sessions
              </button>
            </div>
          </div>
        </div>
      )}
      {mergeState && (
        <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !mergeState.busy && setMergeState(null)}>
          <div className="modal-safe bg-[#FDFCF8] rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="kid-merge-modal">
            <div className="px-5 py-3 border-b hairline flex items-center justify-between">
              <h2 className="font-serif-display text-lg">{mergeState.kid.emoji} Delete {mergeState.kid.name}?</h2>
              <button onClick={() => !mergeState.busy && setMergeState(null)} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="kid-merge-close"><X size={18} /></button>
            </div>
            <div className="p-5">
              <div className="bg-[#F8F1E2] border border-[#EAD9AE] rounded-lg p-3 text-sm text-[#1C1B1A]" data-testid="kid-merge-summary">
                {mergeState.kid.name} earned <strong>{mergeState.info.summary.points} points</strong> from{" "}
                <strong>{mergeState.info.summary.chore_count} chore{mergeState.info.summary.chore_count === 1 ? "" : "s"}</strong>
                {mergeState.info.summary.badge_count > 0 && <> and <strong>{mergeState.info.summary.badge_count} badge{mergeState.info.summary.badge_count === 1 ? "" : "s"}</strong></>}.
                Move it to a family member so nothing is lost?
              </div>
              <label className="block mt-4 text-[11px] uppercase tracking-[0.16em] text-[#6B6A68]">Move to
                <select value={mergeState.targetId} onChange={(e) => setMergeState({ ...mergeState, targetId: e.target.value })}
                  data-testid="kid-merge-target"
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]">
                  {mergeState.info.candidates.map((c) => (
                    <option key={c.user_id} value={c.user_id}>{c.name}{c.is_child ? " (family-restricted)" : ""} · {c.email}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-2 mt-5">
                <button onClick={doMigrate} disabled={mergeState.busy} data-testid="kid-merge-move-btn"
                  className="text-sm px-4 py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
                  {mergeState.busy ? "Working…" : "Move points & delete profile"}
                </button>
                <button onClick={doPlainDelete} disabled={mergeState.busy} data-testid="kid-merge-just-delete-btn"
                  className="text-xs px-4 py-2 rounded-md border hairline text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-50">
                  No — just delete (lose the points)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* pt89T (Feb 20, 2026, Randy) — Master Emoji Generator modal. Fires
          when the user taps the emoji square in the kid-add row. Pick =
          fills the square. Modal auto-closes so the user can complete the
          rest of the add row (name / PIN) and hit Add kid. */}
      <MasterEmojiGeneratorModal
        siloId={siloId}
        open={megOpen}
        onClose={() => setMegOpen(false)}
        onPick={(e) => { if (editKid) setEditKid((k) => ({ ...k, emoji: e })); else setEmoji(e); }}
        preserveParent={true}
        title="Master Emoji Generator"
      />
    </div>
  );
}
